import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import RoomPlannerPdfLayout from './components/job/RoomPlannerPdfLayout';
import {PDFPreview, PDFPreviewHeader} from '@cabinetsbycomputer/pdf-preview';
import {JobProvider} from 'contexts';
import RoomPlannerStandaloneWrapper from './useRoomPlannerStandaloneWrapper';
import {PdfType, usePdfLoadingPrompt} from 'shared/helpers/usePdfLoadingPrompt';
import {RoomPlannerProvider} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import styled from 'styled-components';
import {SnapshotLocations} from 'components/customer/RoomPlannerPDF/constants/SnapshotLocations';

const RoomPlannerPdf = () => {
    const [params] = useSearchParams({pdf: 'true'});
    const [initialized, setInitialized] = useState(false);
    const {jobId, roomId} = useParams<{jobId: string; roomId: string}>();

    const {
        viewPdf,
        pdfDialog,
        isLoading: isPdfLoading,
    } = usePdfLoadingPrompt(PdfType.ROOM_PLANNER);

    const isPreview = useMemo(() => {
        return ['false', '0'].includes(params.get('pdf') || '');
    }, [params]);

    const ref = useRef<HTMLDivElement>(null);

    const pages: {
        title?: string;
        subTitle: string;
        url: string;
    }[] = [
        {
            subTitle: 'PLAN VIEW',
            url: localStorage.getItem(SnapshotLocations['KITCHEN_PLAN']),
        },
        {
            subTitle: 'FRONT ELEVATION',
            url: localStorage.getItem(SnapshotLocations['FRONT_ELEVATION']),
        },
        {
            subTitle: 'LEFT ELEVATION',
            url: localStorage.getItem(SnapshotLocations['LEFT_ELEVATION']),
        },
        {
            subTitle: 'RIGHT ELEVATION',
            url: localStorage.getItem(SnapshotLocations['RIGHT_ELEVATION']),
        },
        {
            title: '3D ISOMETRIC',
            subTitle: 'TOP LEFT VIEW',
            url: localStorage.getItem(SnapshotLocations['TOP_LEFT_VIEW']),
        },
        {
            title: '3D ISOMETRIC',
            subTitle: 'TOP RIGHT VIEW',
            url: localStorage.getItem(SnapshotLocations['TOP_RIGHT_VIEW']),
        },
    ];

    const downloadPdfHandler = useCallback(() => {
        viewPdf(Number(roomId));
    }, [roomId]);

    const seen = new Set();
    const uniquePages = pages.filter((item) => {
        if (seen.has(item.title)) return false;
        seen.add(item.subTitle);
        return true;
    });

    return (
        <RoomPlannerProvider>
            <JobProvider jobId={jobId} roomId={roomId}>
                <RoomPlannerStandaloneWrapper setInitialized={setInitialized}>
                    <PDFPreview
                        layout="landscape"
                        pdfHeaderElement={
                            <>
                                <PDFPreviewHeader
                                    title="Cabinetry.Online"
                                    isLoading={isPdfLoading}
                                    downloadHandler={downloadPdfHandler}
                                />
                                {pdfDialog}
                            </>
                        }
                        showAsPDF={initialized}
                        isError={false}
                        pageTitle={'Cabinetry.Online'}>
                        <div
                            ref={ref}
                            className={!isPreview ? 'invisible' : undefined}>
                            {uniquePages.map((item) =>
                                item.url ? (
                                    <RoomPlannerPdfLayout.Page
                                        key={item.subTitle}
                                        subTitle={item.subTitle}>
                                        <StyledImg
                                            src={item.url}
                                            alt={item.subTitle}
                                        />
                                    </RoomPlannerPdfLayout.Page>
                                ) : null
                            )}
                        </div>
                    </PDFPreview>
                </RoomPlannerStandaloneWrapper>
            </JobProvider>
        </RoomPlannerProvider>
    );
};

const StyledImg = styled.img<{alt: string}>`
    page-break-before: always;
    width: 1024px;
    height: 550px;
    max-width: 100%;
    max-height: 100%;
    display: block;
    object-fit: contain;
    ${({alt}) => `
        ${alt === 'RIGHT ELEVATION' ? 'margin-left: 50px;' : ''}
        ${alt === 'LEFT ELEVATION' ? 'margin-left: -50px' : ''}
    `}
`;

export default RoomPlannerPdf;
